import { SlideOverPanel } from "@components/shared/SlideOverPanel";
import { type ReactElement, useCallback, useState } from "react";
import {
  AssessmentExportButton,
  AssessmentSummaryPanel,
  type AssessmentVersionData,
} from "./stages/AssessmentSummaryPanel";

export interface AssessmentSlideOverCandidate {
  id: string;
  candidate: {
    fullName: string;
    referenceNumber?: number;
    currentRoleTitle: string | null;
    currentCompany: string | null;
    location: string | null;
  };
  candidateAssessment: {
    currentVersion: AssessmentVersionData | null;
  } | null;
}

export interface AssessmentSlideOverDimension {
  id: string;
  continuityId: string;
  name: string;
  idealScore: number | null;
}

interface UseAssessmentSlideOverArgs {
  candidates: AssessmentSlideOverCandidate[];
  dimensions: AssessmentSlideOverDimension[];
}

interface UseAssessmentSlideOverResult {
  open: (campaignCandidateId: string) => void;
  close: () => void;
  slideOverElement: ReactElement;
  openId: string | null;
}

// Shared slideover for the full candidate-assessment view (the
// `AssessmentSummaryPanel`). Used by Longlist, Shortlist, the candidate
// detail panel, the assessment stage, and the scoring matrix so all
// drill-downs share one panel contract.
export function useAssessmentSlideOver({
  candidates,
  dimensions,
}: UseAssessmentSlideOverArgs): UseAssessmentSlideOverResult {
  const [openId, setOpenId] = useState<string | null>(null);

  const open = useCallback((id: string) => setOpenId(id), []);
  const close = useCallback(() => setOpenId(null), []);

  const selected = openId == null ? null : (candidates.find((c) => c.id === openId) ?? null);
  const assessment = selected?.candidateAssessment?.currentVersion ?? null;

  const slideOverElement = (
    <SlideOverPanel
      open={openId != null}
      onClose={close}
      title={
        selected?.candidate
          ? `${selected.candidate.fullName}${typeof selected.candidate.referenceNumber === "number" ? ` (#${selected.candidate.referenceNumber})` : ""}`
          : ""
      }
    >
      {selected && assessment ? (
        <AssessmentSummaryPanel
          candidate={selected.candidate}
          assessment={assessment}
          dimensions={dimensions}
          headerActions={<AssessmentExportButton campaignCandidateId={selected.id} />}
        />
      ) : (
        <p className="py-6 text-center text-sm text-text-faint">No assessment data available.</p>
      )}
    </SlideOverPanel>
  );

  return { open, close, slideOverElement, openId };
}
